import React, { useEffect, useMemo, useState } from 'react';

import { IconX } from '@tabler/icons-react';
import { produce } from 'immer';
import { isEqual } from 'lodash';
import { MapRef } from 'react-map-gl/mapbox';

import useReactGA from 'hooks/useReactGA';
import { ProfileErrorMessages } from 'pages/Settings/GeoConfigSettings';
import PinDetail from 'pages/Settings/GeoConfigSettings//GeoMap/ProfileDetail/PinDetail';
import {
  PostGeoConfig,
  PostGeoConfigLocation,
} from 'pages/Settings/GeoConfigSettings/api/useGeoConfig/useGeoConfig';
import useSaveGeoConfig from 'pages/Settings/GeoConfigSettings/api/useSaveGeoConfig/useSaveGeoConfig';
import PinCard from 'pages/Settings/GeoConfigSettings/NewProfileSidebar/PinCard';
import SetPinDetail from 'pages/Settings/GeoConfigSettings/NewProfileSidebar/SetPinDetail';
import { useGeoConfigContext } from 'pages/Settings/GeoConfigSettings/utils/GeoConfigContext';
import Button from '@lotlinx-aws/sparky-component-library/Sparky/Button';
import IconButton from '@lotlinx-aws/sparky-component-library/Sparky/IconButton';
import Message from '@lotlinx-aws/sparky-component-library/Sparky/Message';
import { COLOR } from '@lotlinx-aws/sparky-component-library/Sparky/styles/vars';
import { ChrevronIcon } from '@lotlinx-aws/sparky-component-library/Sparky/SVGs/ChevronIcon';
import { HamburgerIcon } from '@lotlinx-aws/sparky-component-library/Sparky/SVGs/HamburgerIcon';
import { PlusIcon } from '@lotlinx-aws/sparky-component-library/Sparky/SVGs/PlusIcon';
import { TrashCan } from '@lotlinx-aws/sparky-component-library/Sparky/SVGs/TrashCan';
import Text from '@lotlinx-aws/sparky-component-library/Sparky/Text';
import { useToast } from '@lotlinx-aws/sparky-component-library/Sparky/Toast';

import styles from './ProfileDetail.module.scss';

export interface ProfileDetailProps {
  mapRef: React.MutableRefObject<MapRef | undefined>;
  refetch(): void;
  defaultGeoConfig?: PostGeoConfig;
  isProfileDetailOpen: boolean;
  setIsProfileDetailOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSelectPin(location: PostGeoConfigLocation): void;
}

/**
 * ProfileDetail
 */
export default function ProfileDetail({
  mapRef,
  refetch,
  defaultGeoConfig,
  isProfileDetailOpen,
  setIsProfileDetailOpen,
  onSelectPin,
}: ProfileDetailProps) {
  const [isAddingPin, setIsAddingPin] = useState<boolean>(false);
  const [pinDetail, setPinDetail] = useState<PostGeoConfigLocation>();
  const [pinCardDisable, setPinCardDisable] = useState<boolean>(false);
  const [showMessage, setShowMessage] = useState<'info' | 'warning'>();
  const {
    initialGeoConfig,
    lastSavedTempGeoConfig,
    setLastSavedTempGeoConfig,
    tempGeoConfigs,
    editingPin,
    setEditingPin,
    selectProfile,
    setSelectProfile,
    setTempGeoConfigs,
    selectProfileIndex,
    setSelectPin,
    selectPinIndex,
    modifyingExistingProfile,
    setModifyingExistingProfile,
    errorMessage,
    setErrorMessage,
    onLongChange,
    onRadiusChange,
    onLatChange,
  } = useGeoConfigContext();
  const { addReactGA } = useReactGA();
  const pinsLength = useMemo(
    () => (selectProfileIndex >= 0 ? tempGeoConfigs[selectProfileIndex].locations.length : 0),
    [tempGeoConfigs, selectProfileIndex],
  );

  const newPin = useMemo(
    () => ({
      name: `Pin ${
        selectProfileIndex >= 0
          ? Number(
              tempGeoConfigs[selectProfileIndex].locations[pinsLength - 1].name.split(' ')[1],
            ) + 1
          : 1
      }`,
      latitude: defaultGeoConfig?.locations[0].latitude ?? 0,
      longitude: defaultGeoConfig?.locations[0].longitude ?? 0,
      radius: defaultGeoConfig?.locations[0].radius ?? 30,
    }),
    [mapRef, tempGeoConfigs, selectProfileIndex],
  );

  const { addToast } = useToast();
  const mutation = useSaveGeoConfig();

  const isProfileChanged = !isEqual(tempGeoConfigs, initialGeoConfig);

  const isDefault = useMemo(() => selectProfileIndex === 0, [selectProfileIndex]);

  useEffect(() => {
    setModifyingExistingProfile(false);
    setPinCardDisable(false);
    setShowMessage(undefined);
  }, [selectProfile?.configId, selectProfile?.name]);

  const onMouseEnter = (location: PostGeoConfigLocation) => {
    setSelectPin(location);
    setPinDetail(location);
  };

  const onMouseLeave = () => {
    setSelectPin(undefined);
    setPinDetail(undefined);
  };

  const onClickPinCard = (location: PostGeoConfigLocation) => {
    onSelectPin(location);
    if (modifyingExistingProfile) {
      setLastSavedTempGeoConfig(tempGeoConfigs);
      setSelectPin(location);
      setEditingPin(location);
      setShowMessage('info');
      setPinCardDisable(true);
    } else {
      setPinCardDisable(false);
    }
  };

  const onDiscardPin = () => {
    setIsAddingPin(false);
    setPinCardDisable(false);
    setEditingPin(undefined);
    setSelectPin(undefined);
    setPinDetail(undefined);
    setTempGeoConfigs(lastSavedTempGeoConfig);
  };

  const onAddPin = () => {
    if (!(errorMessage.pinRadius || errorMessage.pinLatitude || errorMessage.pinLongitude)) {
      setSelectPin(undefined);
      setEditingPin(undefined);
      setIsAddingPin(false);
      setPinDetail(undefined);
      setPinCardDisable(false);
    }
  };

  const onSavePin = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!(errorMessage.pinRadius || errorMessage.pinLatitude || errorMessage.pinLongitude)) {
      setEditingPin(undefined);
      setSelectPin(undefined);
      setPinDetail(undefined);
      setPinCardDisable(false);
    }
  };

  const onDeletePin = () => {
    selectPinIndex !== undefined &&
      setTempGeoConfigs(
        produce(tempGeoConfigs, (draft) => {
          draft[selectProfileIndex].locations.splice(selectPinIndex, 1);
          if (draft[selectProfileIndex].locations.length < 1) {
            draft[selectProfileIndex].locations.push(newPin);
            onSelectPin(newPin);
            setSelectPin(newPin);
            setEditingPin(newPin);
            setIsAddingPin(true);
          } else {
            setIsAddingPin(false);
          }
          return draft;
        }),
      );
    setPinCardDisable(false);
  };

  const addAnotherPin = (pin: PostGeoConfigLocation) => {
    setPinCardDisable(true);
    setIsAddingPin(true);
    setShowMessage('warning');
    setLastSavedTempGeoConfig(tempGeoConfigs);
    setTempGeoConfigs(
      produce(tempGeoConfigs, (draft) => {
        if (selectProfileIndex) {
          draft[selectProfileIndex].locations.push(pin);
        }
        return draft;
      }),
    );
    setSelectProfile(
      produce(selectProfile, (draft) => {
        draft?.locations.push(pin);
        return draft;
      }),
    );
    setSelectPin(pin);
    setEditingPin(pin);
  };

  const onCancelProfile = () => {
    setModifyingExistingProfile(false);
    setShowMessage(undefined);
    setSelectPin(undefined);
    setEditingPin(undefined);
    setPinDetail(undefined);
    setPinCardDisable(false);
    setIsAddingPin(false);
    setTempGeoConfigs(initialGeoConfig);
    setErrorMessage({});
  };

  const onDiscardProfile = () => {
    setModifyingExistingProfile(false);
    setShowMessage(undefined);
    setSelectPin(undefined);
    setEditingPin(undefined);
    setPinDetail(undefined);
    setPinCardDisable(false);
    setIsAddingPin(false);
    setTempGeoConfigs(initialGeoConfig);
    setErrorMessage({});
  };

  const onSaveProfile = () => {
    if (isAddingPin) {
      setErrorMessage(
        produce(errorMessage, (draft) => {
          draft.addPinBeforeSaveProfile = ProfileErrorMessages.addPinBeforeSaveProfile;
        }),
      );
    } else if (Object.keys(errorMessage).length === 0) {
      setPinCardDisable(false);
      mutation.mutate(tempGeoConfigs[selectProfileIndex], {
        onSuccess: () => {
          refetch();

          addReactGA('vm_geo_config_save');
          addToast({ status: 'success', description: 'Changes saved!' });
          setPinDetail(undefined);
          setModifyingExistingProfile(false);
          setSelectPin(undefined);
          setEditingPin(undefined);
          setShowMessage(undefined);
          setErrorMessage({});
        },
        onError: () => {
          addToast({ status: 'error', description: 'Failed to save – please try again' });
          setPinDetail(undefined);
        },
      });
    }
  };

  const pinState = (pin: PostGeoConfigLocation): 'New' | 'Modified' | undefined => {
    const initialPin = initialGeoConfig[selectProfileIndex].locations.find(
      (location) => location.id === pin.id && location.name === pin.name,
    );
    if (initialPin) {
      return isEqual(pin, initialPin) ? undefined : 'Modified';
    } else {
      return 'New';
    }
  };

  if (!selectProfile) {
    return null;
  }

  if (!isProfileDetailOpen) {
    return (
      <div className={styles.profileDetailIcon}>
        <IconButton
          onClick={() => setIsProfileDetailOpen(true)}
          aria-label='toggle-control-panel'
          size='lg'
          variants='outline'
        >
          <HamburgerIcon />
        </IconButton>
        <Text size='20' fontWeight={7}>
          {selectProfile?.name}
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.profileDetail}>
      <IconButton
        aria-label='toggle-control-panel'
        onClick={() => setIsProfileDetailOpen(false)}
        size='lg'
        variants='outline'
        className={styles.closeProfileDetailIcon}
      >
        <ChrevronIcon />
      </IconButton>
      <div className={styles.profileDetailHeader}>
        <Text size='20' fontWeight={7} color='secondary' alignment='left'>
          {selectProfile?.name}
        </Text>
        {!isDefault &&
          (modifyingExistingProfile ? (
            <IconButton
              aria-label='close modify profile'
              size='md'
              onClick={() => {
                if (isProfileChanged) {
                  onDiscardProfile();
                } else {
                  onCancelProfile();
                }
              }}
            >
              <IconX size={20} />
            </IconButton>
          ) : (
            <Button variant='text' onClick={() => setModifyingExistingProfile(true)}>
              Edit profile
            </Button>
          ))}
      </div>
      <div className={styles.profileDetailContent}>
        <div className={styles.profilePinList}>
          {tempGeoConfigs[selectProfileIndex].locations.map((location, index) => {
            return index === pinsLength - 1 && isAddingPin && editingPin ? null : (
              <div key={index} className={styles.profilePin}>
                <PinCard
                  pinName={location.name}
                  editable={modifyingExistingProfile}
                  disable={
                    !(editingPin?.id === location.id && editingPin?.name === location.name) &&
                    pinCardDisable
                  }
                  showDoneEditingButton={
                    modifyingExistingProfile &&
                    editingPin?.id === location.id &&
                    editingPin?.name === location.name
                  }
                  state={pinState(location)}
                  onClick={() => onClickPinCard(location)}
                  onMouseEnter={() => onMouseEnter(location)}
                  onMouseLeave={() => onMouseLeave()}
                  onSavePin={onSavePin}
                />
                {!pinCardDisable &&
                  pinDetail?.id === location.id &&
                  pinDetail?.name === location.name && (
                    <div className={styles.pinDetailsWrapper}>
                      <PinDetail location={location} />
                    </div>
                  )}
                {modifyingExistingProfile &&
                  !isDefault &&
                  !isAddingPin &&
                  editingPin &&
                  pinCardDisable &&
                  editingPin?.id === location.id &&
                  editingPin?.name === location.name && (
                    <div className={styles.modifyPinDetail}>
                      <SetPinDetail
                        editingPin={editingPin}
                        onLongChange={onLongChange}
                        onLatChange={onLatChange}
                        onRadiusChange={onRadiusChange}
                        errorMessage={errorMessage}
                      />
                      <Button
                        leftIcon={<TrashCan color={COLOR.SPARKY_RED_400} />}
                        category='danger'
                        contentClassName={styles.deletePinButtonContent}
                        className={styles.deletePinButton}
                        onClick={onDeletePin}
                      >
                        Delete pin
                      </Button>
                    </div>
                  )}
              </div>
            );
          })}
          {modifyingExistingProfile && !isDefault && isAddingPin && editingPin && (
            <div className={styles.addPinPanel}>
              <Text size='16' fontWeight={4} color='secondary'>
                Add new pin to profile
              </Text>
              <SetPinDetail
                editingPin={editingPin}
                onLongChange={onLongChange}
                onLatChange={onLatChange}
                onRadiusChange={onRadiusChange}
                errorMessage={errorMessage}
              />
              <div className={styles.addPinButtonsWrapper}>
                <Button size='sm' variant='outline' onClick={onDiscardPin}>
                  Discard
                </Button>
                <Button size='sm' variant='solid' onClick={onAddPin}>
                  Add pin
                </Button>
              </div>
              {errorMessage.addPinBeforeSaveProfile && (
                <Message dismissable={false} variant='muted' category='danger'>
                  {errorMessage.addPinBeforeSaveProfile}
                </Message>
              )}
            </div>
          )}
        </div>
        {modifyingExistingProfile && !isDefault && !isAddingPin && (
          <Button
            variant='outline'
            leftIcon={<PlusIcon />}
            onClick={() => addAnotherPin(newPin)}
            className={styles.addAnotherPin}
          >
            Add another pin
          </Button>
        )}
        {isDefault && (
          <Message
            category='default'
            dismissable={false}
            variant='muted'
            heading={`Default radius profile is always set to ${tempGeoConfigs[0].locations[0].radius} mi around your dealership`}
          >
            If you'd like your campaigns to target other areas, create a new profile and select it
            when building a campaign.
          </Message>
        )}
        {showMessage && modifyingExistingProfile && !isDefault && (
          <Message
            category={showMessage}
            variant='muted'
            heading='Campaigns using this profile will be affected'
          >
            Changes you make to this profile will be applied to any campaigns currently using it.
          </Message>
        )}
      </div>
      {modifyingExistingProfile &&
        (isProfileChanged ? (
          <div className={styles.profileDetailFooter}>
            <Button variant='outline' onClick={onDiscardProfile}>
              Discard changes
            </Button>
            <Button
              variant='solid'
              onClick={onSaveProfile}
              data-track='settings_geo_config_save'
              data-track-section='settings'
              data-track-target='submit'
            >
              Save
            </Button>
          </div>
        ) : (
          <Button variant='outline' onClick={onCancelProfile} className={styles.cancelButton}>
            Cancel
          </Button>
        ))}
    </div>
  );
}
